<template>
  <div class="yl-root">
    <div class="yl-container">
      <!-- ===== Intro ===== -->
      <section class="yl-intro" id="intro">
        <div class="glow-orb orb-1"></div>
        <div class="glow-orb orb-2"></div>

        <span class="sparkle" style="top: 15%; left: 10%; --duration: 3s; --delay: 0s; font-size: 20px">✨</span>
        <span class="sparkle" style="top: 22%; right: 12%; --duration: 2.5s; --delay: 1s; font-size: 24px">✨</span>
        <span class="sparkle sparkle-light" style="top: 60%; left: 18%; --duration: 4s; --delay: 2s; font-size: 18px">🌸</span>
        <span class="sparkle" style="top: 50%; right: 20%; --duration: 3.5s; --delay: 0.5s; font-size: 20px">✨</span>
        <span class="sparkle" style="bottom: 25%; left: 25%; --duration: 4s; --delay: 1.5s; font-size: 16px">🌸</span>

        <div class="intro-content">
          <div class="festival-badge">✨ LAFIA 릴스 페스티벌 ✨</div>
          <img class="intro-logo" :src="require('@/assets/logo/lafia_logo_main.png')" alt="LAFIA" loading="eager" />
          <p class="intro-tagline">
            나만의 동화 속 주인공이 되는 곳<br />
            <strong class="highlight">대한민국 1위 공주스튜디오</strong><br />
            <span class="since">since 2018</span>
          </p>
          <div class="festival-btn">
            <a href="#tabNav" @click.prevent="switchTab('cast')">🎬 주인공으로 지원하기</a>
          </div>
        </div>
        <div class="intro-divider"></div>
      </section>

      <!-- ===== Tab Nav ===== -->
      <nav class="yl-tab-nav" id="tabNav" role="tablist">
        <button v-for="tab in TABS" :key="tab.id" class="yl-tab-btn" :class="{ active: activeTab === tab.id }" role="tab" :aria-selected="activeTab === tab.id" @click="switchTab(tab.id)">
          <span v-if="tab.icon" class="yl-tab-icon">{{ tab.icon }}</span>
          {{ tab.label }}
        </button>
      </nav>

      <!-- ===== Gallery Tab ===== -->
      <div v-if="activeTab === 'premium'" class="yl-gallery-section">
        <!-- Skeleton -->
        <template v-if="loading">
          <div class="yl-skeleton-grid">
            <div v-for="n in 6" :key="n" class="yl-skeleton-card">
              <div class="yl-skeleton-img"></div>
              <div class="yl-skeleton-body">
                <div class="yl-skeleton-line short"></div>
                <div class="yl-skeleton-line"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Error -->
        <div v-else-if="error" class="yl-gallery-error">
          <p>컨셉 정보를 불러오지 못했습니다</p>
          <button class="yl-retry-btn" @click="loadConcepts">다시 시도</button>
        </div>

        <!-- Content -->
        <template v-else>
          <!-- Category Filter -->
          <div class="yl-category-filter">
            <button v-for="tag in tagList" :key="tag.id" class="yl-filter-chip" :class="{ active: activeTag === tag.id }" @click="activeTag = tag.id">
              {{ tag.title }}
            </button>
          </div>

          <!-- Count -->
          <div class="yl-gallery-count">{{ filteredConcepts.length }}개의 컨셉</div>

          <!-- Grid -->
          <div v-if="filteredConcepts.length" class="yl-card-grid">
            <router-link v-for="item in filteredConcepts" :key="item.id" :to="getConceptLink(item.id)" class="yl-concept-card">
              <div class="yl-card-img-wrap">
                <img :src="'/upload/concept/thumbnail_image/' + item.pattern_1_image" :alt="item.title" loading="lazy" />
              </div>
              <div class="yl-card-body">
                <div v-if="item.tag" class="yl-card-tags">
                  <span v-for="t in item.tag.split(',')" :key="t" class="yl-tag-chip">{{ t.trim() }}</span>
                </div>
                <div class="yl-card-title">{{ item.title }}</div>
              </div>
            </router-link>
          </div>

          <div v-else class="yl-gallery-error">
            <p>해당 조건의 컨셉이 없습니다</p>
          </div>
        </template>
      </div>

      <!-- ===== Cast Form Tab ===== -->
      <div v-if="activeTab === 'cast'" class="yl-cast-section">
        <!-- Success -->
        <div v-if="castSubmitted" class="yl-cast-success">
          <div class="yl-success-icon">📸</div>
          <h3>신청이 완료되었습니다!</h3>
          <p>라피아 팀에서 검토 후 연락드릴게요.<br />함께 멋진 콘텐츠를 만들어가요 ✨</p>
          <div class="yl-cast-success-actions">
            <button class="yl-btn-secondary" @click="switchTab('premium')">갤러리로 돌아가기</button>
            <button class="yl-btn-primary" @click="resetCastForm">추가 신청</button>
          </div>
        </div>

        <!-- Form -->
        <template v-else>
          <div class="yl-cast-title">출연자 · MC 모집</div>
          <div class="yl-cast-subtitle">라피아 릴스 콘텐츠 함께할 분을 찾습니다 ✨</div>

          <form class="yl-cast-form" @submit.prevent="submitCast" novalidate>
            <!-- 역할 -->
            <div class="yl-form-group">
              <span class="yl-form-label">역할 선택 <span class="required">*</span></span>
              <div class="yl-role-cards">
                <button type="button" class="yl-role-card" :class="{ selected: castForm.role === 'mc' }" @click="castForm.role = 'mc'">
                  <span class="yl-role-icon">🎤</span>
                  MC
                  <span class="yl-role-desc">행사 진행·리포팅</span>
                </button>
                <button type="button" class="yl-role-card" :class="{ selected: castForm.role === 'talent' }" @click="castForm.role = 'talent'">
                  <span class="yl-role-icon">⭐</span>
                  출연자
                  <span class="yl-role-desc">촬영 모델·참여자</span>
                </button>
              </div>
            </div>

            <!-- 이름 -->
            <div class="yl-form-group">
              <label class="yl-form-label" for="castName">이름 (닉네임) <span class="required">*</span></label>
              <input id="castName" v-model="castForm.name" type="text" class="yl-form-input" maxlength="20" placeholder="이름 또는 닉네임" />
            </div>

            <!-- 연락처 -->
            <div class="yl-form-group">
              <label class="yl-form-label" for="castContact">연락처 <span class="required">*</span></label>
              <input id="castContact" v-model="castForm.contact" type="text" class="yl-form-input" placeholder="@인스타그램ID 또는 010-xxxx-xxxx" />
            </div>

            <!-- 연령대 -->
            <div class="yl-form-group">
              <label class="yl-form-label" for="castAge">연령대 <span class="required">*</span></label>
              <select id="castAge" v-model="castForm.age_range" class="yl-form-select">
                <option value="">선택해주세요</option>
                <option value="10대">10대</option>
                <option value="20대">20대</option>
                <option value="30대">30대</option>
                <option value="40대 이상">40대 이상</option>
              </select>
            </div>

            <!-- 보호자 정보 (10대) -->
            <div v-if="castForm.age_range === '10대'" class="yl-guardian-section">
              <span class="yl-guardian-label">👨‍👩‍👧 보호자 정보 (10대 필수)</span>
              <input v-model="castForm.guardian_contact" type="text" class="yl-form-input" placeholder="보호자 전화번호 (010-xxxx-xxxx)" />
              <div class="yl-checkbox-group">
                <input id="guardianConsent" v-model="castForm.guardian_consent" type="checkbox" />
                <label for="guardianConsent">보호자가 본 신청에 동의합니다</label>
              </div>
            </div>

            <!-- 나만의 특징 -->
            <div class="yl-form-group">
              <label class="yl-form-label" for="castTrait">나만의 특징 <span class="required">*</span></label>
              <textarea
                id="castTrait"
                v-model="castForm.my_trait"
                class="yl-form-textarea"
                placeholder='나만의 특징을 자유롭게 적어주세요 ✨&#10;예: "키 152cm, 이목구비 뚜렷해요" / "긴 머리 직장인"&#10;"댄스 경력 5년" / "행사 진행 경험 3회 (MC 지원 시)"&#10;당신의 독특함이 새로운 콘텐츠의 시작이 됩니다.'
              ></textarea>
              <span class="yl-form-hint">최소 10자 이상</span>
            </div>

            <!-- 사진 업로드 -->
            <div class="yl-form-group">
              <span class="yl-form-label">사진 (1~3장) <span class="required">*</span></span>
              <div class="yl-photo-upload-area">
                <div v-for="(photo, i) in castPhotos" :key="'photo-' + i" class="yl-photo-slot filled">
                  <img :src="photo.url" :alt="'사진 ' + (i + 1)" />
                  <div v-if="photo.uploading" class="yl-photo-uploading">
                    <span class="yl-spinner" style="width: 14px; height: 14px; margin: 0"></span>
                  </div>
                  <button v-else type="button" class="yl-photo-remove" @click="removePhoto(i)">×</button>
                </div>
                <div v-if="castPhotos.length < 3" class="yl-photo-slot" @click="$refs.photoInput.click()">
                  <span class="yl-photo-add">+</span>
                </div>
              </div>
              <span class="yl-form-hint">얼굴이 잘 보이는 사진을 올려주세요. 자동 압축됩니다.</span>
              <input ref="photoInput" type="file" accept="image/jpeg,image/png,image/webp" multiple style="display: none" @change="handlePhotoInput" />
            </div>

            <!-- 영상 링크 -->
            <div class="yl-form-group">
              <label class="yl-form-label" for="castVideo">영상 링크 (선택)</label>
              <input id="castVideo" v-model="castForm.video_url" type="text" class="yl-form-input" placeholder="인스타/유튜브/틱톡 URL" />
            </div>

            <!-- 개인정보 동의 -->
            <div class="yl-form-group">
              <div class="yl-privacy-text">
                [수집 항목] 이름(닉네임), 연락처, 연령대, 보호자 연락처(해당 시), 사진, 영상 링크<br />
                [이용 목적] 라피아 콘텐츠 출연 검토 및 연락<br />
                [보관 기간] 1년 (이후 안전 폐기)
              </div>
              <div class="yl-checkbox-group">
                <input id="privacyConsent" v-model="castForm.privacy_consent" type="checkbox" />
                <label for="privacyConsent">위 내용에 동의합니다 <span class="required">*</span></label>
              </div>
            </div>

            <button type="submit" class="yl-cast-submit" :disabled="castSubmitting">
              <span v-if="castSubmitting" class="yl-spinner"></span>
              {{ castSubmitting ? "처리 중..." : "신청하기" }}
            </button>
          </form>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { AllTag, Tags } from "@/constant"

const TABS = [
  { id: "premium", label: "프리미엄 컨셉", icon: "👑", productType: "premium" },
  { id: "cast", label: "출연 신청", icon: "🎬" },
]

function defaultCastForm() {
  return {
    role: "",
    name: "",
    contact: "",
    age_range: "",
    guardian_contact: "",
    guardian_consent: false,
    my_trait: "",
    video_url: "",
    privacy_consent: false,
  }
}

export default {
  name: "WebsiteYoutubeLink",

  data: () => ({
    TABS,
    activeTab: TABS[0].id,

    // Gallery
    concepts: [],
    loading: false,
    error: false,
    activeTag: "all",

    // Cast form
    castForm: defaultCastForm(),
    castPhotos: [], // { url: string, filename: string, uploading: boolean }
    castSubmitting: false,
    castSubmitted: false,
  }),

  computed: {
    tagList() {
      const usedTags = new Set()
      this.concepts.forEach((c) => {
        if (c.tag) c.tag.split(",").forEach((t) => usedTags.add(t.trim()))
      })
      return [AllTag, ...Tags.filter((t) => usedTags.has(t.title))]
    },

    filteredConcepts() {
      if (this.activeTag === "all") return this.concepts
      const tagTitle = Tags.find((t) => t.id === this.activeTag)?.title
      if (!tagTitle) return this.concepts
      return this.concepts.filter(
        (c) =>
          c.tag &&
          c.tag
            .split(",")
            .map((t) => t.trim())
            .includes(tagTitle)
      )
    },
  },

  mounted() {
    this.loadConcepts()
  },

  methods: {
    switchTab(tabId) {
      this.activeTab = tabId
      if (tabId !== "cast") {
        this.loadConcepts()
      }
    },

    loadConcepts() {
      if (this.activeTab !== "premium") return
      this.loading = true
      this.error = false
      this.$http
        .post("/api/concept/select", {
          params: { productType: "premium" },
        })
        .then((res) => {
          this.concepts = res.data || []
        })
        .catch(() => {
          this.error = true
        })
        .finally(() => {
          this.loading = false
        })
    },

    getConceptLink(conceptId) {
      const query = {}
      if (this.$route.query.event) {
        query.event = this.$route.query.event
      }
      return { path: `/castle/${conceptId}`, query }
    },

    async handlePhotoInput(e) {
      const files = Array.from(e.target.files)
      for (const file of files) {
        if (this.castPhotos.length >= 3) break
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) continue
        const preview = URL.createObjectURL(file)
        const idx = this.castPhotos.length
        this.castPhotos.push({ url: preview, filename: null, uploading: true })
        try {
          const blob = await this.compressImage(file)
          const formData = new FormData()
          formData.append("image", blob, file.name)
          const res = await this.$http.post("/api/image/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            params: { type: "website_youtube_link" },
          })
          this.castPhotos[idx].filename = res.data
          this.castPhotos[idx].uploading = false
          URL.revokeObjectURL(preview)
          this.castPhotos[idx].url = "/upload/website_youtube_link/" + res.data
        } catch (err) {
          console.error("Photo upload error:", err)
          this.castPhotos.splice(idx, 1)
          alert("사진 업로드에 실패했습니다. 다시 시도해주세요.")
        }
      }
      e.target.value = ""
    },

    removePhoto(index) {
      this.castPhotos.splice(index, 1)
    },

    compressImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        const objectUrl = URL.createObjectURL(file)
        img.onload = () => {
          URL.revokeObjectURL(objectUrl)
          const MAX = 1200
          let w = img.width,
            h = img.height
          if (w > MAX || h > MAX) {
            if (w > h) {
              h = Math.round((h * MAX) / w)
              w = MAX
            } else {
              w = Math.round((w * MAX) / h)
              h = MAX
            }
          }
          const canvas = document.createElement("canvas")
          canvas.width = w
          canvas.height = h
          canvas.getContext("2d").drawImage(img, 0, 0, w, h)
          canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/jpeg", 0.7)
        }
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          reject(new Error("Image load failed"))
        }
        img.src = objectUrl
      })
    },

    async submitCast() {
      // Validate
      const errors = []
      if (!this.castForm.role) errors.push("역할을 선택해주세요.")
      if (!this.castForm.name || this.castForm.name.length > 20) errors.push("이름을 입력해주세요. (1~20자)")
      if (!this.castForm.contact) errors.push("연락처를 입력해주세요.")
      if (!this.castForm.age_range) errors.push("연령대를 선택해주세요.")
      if (this.castForm.age_range === "10대") {
        if (!this.castForm.guardian_contact) errors.push("보호자 연락처를 입력해주세요.")
        if (!this.castForm.guardian_consent) errors.push("보호자 동의가 필요합니다.")
      }
      if (!this.castForm.my_trait || this.castForm.my_trait.length < 10) errors.push("나만의 특징을 10자 이상 입력해주세요.")
      if (this.castPhotos.length === 0) errors.push("사진을 최소 1장 첨부해주세요.")
      if (this.castPhotos.some((p) => p.uploading)) errors.push("사진 업로드가 완료될 때까지 기다려주세요.")
      if (!this.castForm.privacy_consent) errors.push("개인정보 수집에 동의해주세요.")

      if (errors.length > 0) {
        alert(errors[0])
        return
      }

      this.castSubmitting = true
      try {
        const res = await this.$http.post("/api/website_youtube_link/insert", {
          params: {
            role: this.castForm.role,
            name: this.castForm.name,
            contact: this.castForm.contact,
            age_range: this.castForm.age_range,
            guardian_contact: this.castForm.age_range === "10대" ? this.castForm.guardian_contact : null,
            my_trait: this.castForm.my_trait,
            photos: this.castPhotos.map((p) => p.filename),
            video_url: this.castForm.video_url || null,
          },
        })
        if (res.data.success) {
          alert("등록되었습니다.")
          this.castForm = defaultCastForm()
          this.castPhotos = []
        } else {
          alert("신청 중 오류가 발생했습니다. 다시 시도해주세요.")
        }
      } catch (err) {
        console.error("Submit error:", err)
        alert("네트워크 오류가 발생했습니다. 다시 시도해주세요.")
      } finally {
        this.castSubmitting = false
      }
    },

    resetCastForm() {
      this.castSubmitted = false
      this.castForm = defaultCastForm()
      this.castPhotos = []
    },
  },
}
</script>

<style scoped>
/* ===== Reset ===== */
.yl-root {
  font-family: "Noto Sans KR", "Apple SD Gothic Neo", sans-serif;
  background: #fdf8f5;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #3d2c2e;
}

.yl-container {
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  background: #faf6f3;
  box-shadow: 0 0 40px rgba(134, 102, 92, 0.08);
  position: relative;
  overflow: hidden;
}

/* ===== CSS Variables ===== */
.yl-container,
.yl-root {
  --color-brand: #86665c;
  --color-brand-light: #a68b82;
  --color-rose: #c4958a;
  --color-cream: #fdf8f5;
  --color-warm-white: #faf6f3;
  --color-text: #3d2c2e;
  --color-text-light: #8b7d76;
  --color-border: #e8ddd8;
  --font-brand: "Cormorant Garamond", Georgia, serif;
  --radius-pill: 24px;
  --transition: 0.3s ease;
}

/* ===== Intro ===== */
.yl-intro {
  padding: 80px 24px 60px;
  text-align: center;
  position: relative;
  background: linear-gradient(135deg, #fffcfb 0%, #fef5ef 50%, #fdf0f2 100%);
  overflow: hidden;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
  z-index: 0;
}

.orb-1 {
  top: -10%;
  left: -10%;
  width: 300px;
  height: 300px;
  background: rgba(196, 149, 138, 0.25);
  animation: floatOrb 10s ease-in-out infinite alternate;
}

.orb-2 {
  bottom: -10%;
  right: -10%;
  width: 250px;
  height: 250px;
  background: rgba(212, 165, 116, 0.25);
  animation: floatOrb 12s ease-in-out infinite alternate-reverse;
}

@keyframes floatOrb {
  0% {
    transform: translateY(0) scale(1);
  }
  100% {
    transform: translateY(-30px) scale(1.1);
  }
}

.intro-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.festival-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 22px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  color: #8a6c62;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  border-radius: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  margin-bottom: 36px;
  opacity: 0;
  animation: dropIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.intro-logo {
  width: 150px;
  height: auto;
  display: block;
  margin-bottom: 28px;
  filter: drop-shadow(0 4px 15px rgba(196, 149, 138, 0.4));
  opacity: 0;
  animation: dropIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s forwards;
}

@keyframes dropIn {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.intro-tagline {
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
  letter-spacing: 0.04em;
  line-height: 1.6;
  margin-bottom: 48px;
  opacity: 0;
  animation: dropIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards;
}

.intro-tagline .highlight {
  font-size: 19px;
  color: var(--color-brand);
  font-weight: 700;
  line-height: 1.8;
  letter-spacing: 0.02em;
}

.intro-tagline .since {
  display: block;
  margin-top: 10px;
  font-style: italic;
  font-family: var(--font-brand);
  font-size: 12px;
  letter-spacing: 0.15em;
  color: var(--color-brand-light);
}

.festival-btn {
  width: 100%;
  max-width: 340px;
  opacity: 0;
  animation: dropIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s forwards;
}

.festival-btn a {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 24px;
  border-radius: 40px;
  background: linear-gradient(135deg, var(--color-brand) 0%, #aa8475 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 25px -5px rgba(134, 102, 92, 0.5);
  transition: all 0.3s ease;
}

.festival-btn a:hover {
  box-shadow: 0 15px 30px -5px rgba(134, 102, 92, 0.6);
  transform: translateY(-3px);
}

.sparkle {
  position: absolute;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  animation: twinkle var(--duration, 3s) ease-in-out infinite;
  animation-delay: var(--delay, 0s);
}

.sparkle-light {
  opacity: 0.6;
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0;
    transform: scale(0.5) rotate(0deg);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.2) rotate(20deg);
  }
}

.intro-divider {
  position: relative;
  z-index: 2;
  margin: 40px auto 0;
  width: 40px;
  height: 1px;
  background: var(--color-border);
  opacity: 0;
  animation: fadeIn 1s ease 0.8s forwards;
}

/* ===== Tab Nav ===== */
.yl-tab-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--color-warm-white);
  padding: 16px 20px 0;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: 1px solid var(--color-border);
}

.yl-tab-nav::-webkit-scrollbar {
  display: none;
}

.yl-tab-btn {
  flex-shrink: 0;
  padding: 10px 20px;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 400;
  color: var(--color-text-light);
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
  letter-spacing: 0.02em;
  margin-bottom: -1px;
}

.yl-tab-btn:hover {
  color: var(--color-brand);
}

.yl-tab-btn.active {
  color: var(--color-brand);
  font-weight: 500;
  border-bottom-color: var(--color-brand);
}

.yl-tab-icon {
  margin-right: 4px;
  font-size: 12px;
}

/* ===== Gallery ===== */
.yl-gallery-section {
  padding: 0 12px 24px;
}

.yl-category-filter {
  display: flex;
  gap: 6px;
  padding: 14px 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.yl-category-filter::-webkit-scrollbar {
  display: none;
}

.yl-filter-chip {
  flex-shrink: 0;
  padding: 6px 16px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border);
  background: transparent;
  font-size: 12px;
  color: var(--color-text-light);
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
}

.yl-filter-chip:hover {
  border-color: var(--color-rose);
  color: var(--color-brand);
}

.yl-filter-chip.active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #fff;
}

.yl-card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.yl-concept-card {
  display: block;
  text-decoration: none;
  color: var(--color-text);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 12px rgba(134, 102, 92, 0.08);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.yl-concept-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(134, 102, 92, 0.15);
}

.yl-card-img-wrap {
  aspect-ratio: 3/4;
  overflow: hidden;
  position: relative;
  background: var(--color-cream);
  border-radius: 12px;
}

.yl-card-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  transition: transform 0.3s ease;
}

.yl-concept-card:hover .yl-card-img-wrap img {
  transform: scale(1.04);
}

.yl-card-body {
  padding: 10px 10px 12px;
}

.yl-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.yl-tag-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 400;
  background: rgba(196, 149, 138, 0.12);
  color: var(--color-brand);
  letter-spacing: 0.02em;
}

.yl-card-title {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--color-text);
}

.yl-gallery-count {
  text-align: right;
  font-size: 11px;
  color: var(--color-text-light);
  padding: 0 2px 8px;
  letter-spacing: 0.02em;
}

/* ===== Skeleton ===== */
.yl-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 14px 0;
}

.yl-skeleton-card {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 12px rgba(134, 102, 92, 0.06);
}

.yl-skeleton-img {
  aspect-ratio: 3/4;
  background: linear-gradient(110deg, #f0ebe8 30%, #f7f3f0 50%, #f0ebe8 70%);
  background-size: 200% 100%;
  animation: shimmer 1.5s linear infinite;
}

.yl-skeleton-body {
  padding: 10px;
}

.yl-skeleton-line {
  height: 10px;
  border-radius: 5px;
  background: linear-gradient(110deg, #f0ebe8 30%, #f7f3f0 50%, #f0ebe8 70%);
  background-size: 200% 100%;
  animation: shimmer 1.5s linear infinite;
  margin-bottom: 6px;
}

.yl-skeleton-line.short {
  width: 60%;
}

/* ===== Error ===== */
.yl-gallery-error {
  text-align: center;
  padding: 60px 20px;
}

.yl-gallery-error p {
  font-size: 13px;
  color: var(--color-text-light);
  margin-bottom: 16px;
}

.yl-retry-btn {
  display: inline-block;
  padding: 10px 24px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: transparent;
  font-size: 12px;
  color: var(--color-brand);
  cursor: pointer;
  transition: var(--transition);
}

.yl-retry-btn:hover {
  background: var(--color-cream);
  border-color: var(--color-rose);
}

/* ===== Cast Form ===== */
.yl-cast-section {
  padding: 20px 16px 40px;
}

.yl-cast-title {
  font-family: var(--font-brand);
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  color: var(--color-text);
  margin-bottom: 4px;
}

.yl-cast-subtitle {
  text-align: center;
  font-size: 12px;
  color: var(--color-text-light);
  margin-bottom: 24px;
  line-height: 1.5;
}

.yl-cast-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.yl-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.yl-form-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text);
}

.required {
  color: var(--color-rose);
  margin-left: 2px;
}

.yl-role-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.yl-role-card {
  padding: 16px 12px;
  border: 2px solid var(--color-border);
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  text-align: center;
  transition: var(--transition);
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.yl-role-card:hover {
  border-color: var(--color-rose);
}

.yl-role-card.selected {
  border-color: var(--color-rose);
  background: linear-gradient(135deg, rgba(196, 149, 138, 0.08), rgba(212, 165, 116, 0.08));
  box-shadow: 0 0 0 1px var(--color-rose);
}

.yl-role-icon {
  font-size: 24px;
  display: block;
  margin-bottom: 6px;
}

.yl-role-desc {
  font-size: 11px;
  font-weight: 300;
  color: var(--color-text-light);
  margin-top: 4px;
}

.yl-form-input,
.yl-form-select,
.yl-form-textarea {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  font-size: 13px;
  color: var(--color-text);
  background: #fff;
  transition: border-color 0.2s ease;
  outline: none;
  font-family: inherit;
}

.yl-form-input:focus,
.yl-form-select:focus,
.yl-form-textarea:focus {
  border-color: var(--color-rose);
}

.yl-form-textarea {
  min-height: 100px;
  resize: vertical;
  line-height: 1.5;
}

.yl-form-hint {
  font-size: 11px;
  color: var(--color-text-light);
  line-height: 1.5;
}

.yl-guardian-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  background: rgba(196, 149, 138, 0.06);
  border-radius: 10px;
  border: 1px solid rgba(196, 149, 138, 0.15);
}

.yl-guardian-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-brand);
}

.yl-photo-upload-area {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.yl-photo-slot {
  width: 80px;
  height: 80px;
  border: 2px dashed var(--color-border);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.2s ease;
  background: #fff;
}

.yl-photo-slot:hover {
  border-color: var(--color-rose);
}

.yl-photo-slot.filled {
  border-style: solid;
  border-color: var(--color-rose);
}

.yl-photo-slot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.yl-photo-add {
  font-size: 24px;
  color: var(--color-text-light);
}

.yl-photo-uploading {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}

.yl-photo-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.yl-checkbox-group {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.yl-checkbox-group input[type="checkbox"] {
  margin-top: 2px;
  accent-color: var(--color-rose);
  flex-shrink: 0;
}

.yl-checkbox-group label {
  font-size: 12px;
  color: var(--color-text);
  line-height: 1.5;
}

.yl-privacy-text {
  font-size: 11px;
  color: var(--color-text-light);
  background: rgba(0, 0, 0, 0.02);
  padding: 10px 12px;
  border-radius: 8px;
  line-height: 1.6;
  margin-bottom: 4px;
}

.yl-cast-submit {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: var(--radius-pill);
  background: linear-gradient(135deg, #c4958a, #d4a574);
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  letter-spacing: 0.03em;
  font-family: inherit;
}

.yl-cast-submit:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.yl-cast-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.yl-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}

/* ===== Cast Success ===== */
.yl-cast-success {
  text-align: center;
  padding: 60px 20px;
}

.yl-success-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.yl-cast-success h3 {
  font-family: var(--font-brand);
  font-size: 20px;
  color: var(--color-text);
  margin-bottom: 8px;
}

.yl-cast-success p {
  font-size: 13px;
  color: var(--color-text-light);
  line-height: 1.6;
  margin-bottom: 24px;
}

.yl-cast-success-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.yl-cast-success-actions button {
  padding: 10px 20px;
  border-radius: var(--radius-pill);
  font-size: 13px;
  cursor: pointer;
  transition: var(--transition);
  font-family: inherit;
}

.yl-btn-primary {
  background: linear-gradient(135deg, #c4958a, #d4a574);
  color: #fff;
  border: none;
}

.yl-btn-secondary {
  background: transparent;
  color: var(--color-brand);
  border: 1px solid var(--color-border);
}

.yl-btn-secondary:hover {
  border-color: var(--color-rose);
}

/* ===== Dev Notice Snackbar ===== */
.yl-snackbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #3d2c2e;
  color: #fff;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 13px;
  white-space: nowrap;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  animation: fadeInUp 0.3s ease;
}

/* ===== Animations ===== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== Desktop ===== */
@media (min-width: 600px) {
  .yl-card-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .intro-logo {
    width: 160px;
  }
}
</style>
